import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { parseCSV, type ParsedData } from "@/lib/csv";
import {
  getTableRowsPayload,
  type TableRowsPayloadRef,
} from "@/lib/table-rows-cache";
import { findExactColumnIndex } from "@/lib/table-utils";

export type VersionType = "generated" | "edited";

export function useSubmittalData(jobId: string) {
  const typedJobId = jobId as Id<"specPilotSubmittals">;

  // Queries
  const { data: job } = useQuery(convexQuery(api.specPilot.submittals.get, {
    submittalId: typedJobId,
  }));
  const { data: submittal } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getSummaryBySubmittal,
      { submittalId: typedJobId }
    )
  );
  const { data: typicals } = useQuery(
    convexQuery(
      api.specPilot.typicalSubmittals.listBySubmittal,
      { submittalId: typedJobId }
    )
  );
  const { data: trackerItems } = useQuery(
    convexQuery(
      api.specPilot.trackerItems.listBySubmittal,
      { submittalId: typedJobId }
    )
  );
  const { data: editedPayloadRef } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getEditedPayloadRef,
      submittal?.hasEditedData ? { submittalId: submittal._id } : "skip"
    )
  );
  const { data: storageUrl } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getStorageUrl,
      submittal?.generatedFileKey
        ? {
            fileKey: submittal?.generatedFileKey,
          }
        : "skip"
    )
  );

  // Local state
  const [activeVersion, setActiveVersion] = useState<VersionType>("generated");
  const [generatedData, setGeneratedData] = useState<ParsedData | null>(null);
  const [editedData, setEditedData] = useState<ParsedData | null>(null);
  const [isHydratingEdited, setIsHydratingEdited] = useState(false);
  const [editedPayloadError, setEditedPayloadError] = useState<string | null>(
    null
  );
  const [isLoadingCSV, setIsLoadingCSV] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  // Signed download URLs rotate on every Convex re-evaluation. Key fetches by
  // stable file/version identity so URL churn does not remount the page.
  const fetchedGeneratedFileKeyRef = useRef<string | null>(null);
  const hydratedEditedRef = useRef<{ tableId: string; rowsVersion: number } | null>(
    null
  );
  const hasInitializedVersionRef = useRef(false);

  const hasEditedData = Boolean(submittal?.hasEditedData || editedData);
  const generatedFileKey = submittal?.generatedFileKey ?? null;

  // Route param changes reuse this hook instance — clear load bookkeeping.
  useEffect(() => {
    fetchedGeneratedFileKeyRef.current = null;
    hydratedEditedRef.current = null;
    hasInitializedVersionRef.current = false;
    setGeneratedData(null);
    setEditedData(null);
    setEditedPayloadError(null);
    setCsvError(null);
    setIsLoadingCSV(false);
    setIsHydratingEdited(false);
    setActiveVersion("generated");
  }, [typedJobId]);

  const currentData: ParsedData | null =
    activeVersion === "edited" ? editedData : generatedData;

  // Merge in typical rows not already present in the active dataset
  const displayData = useMemo((): ParsedData | null => {
    if (!currentData) return null;

    // Find the title column using multiple possible header names
    const titleCol = findExactColumnIndex(currentData.headers, ["Title", "Submittal Title"]);
    const existingTitles = new Set<string>();

    if (titleCol >= 0) {
      for (const row of currentData.rows) {
        const title = row[titleCol]?.trim();
        if (title) {
          existingTitles.add(title);
        }
      }
    }

    const typicalRows = (typicals ?? []).flatMap(
      (typical: { title: string }) => {
        if (titleCol < 0 || existingTitles.has(typical.title)) {
          return [];
        }

        const row = Array(currentData.headers.length).fill("");
        row[titleCol] = typical.title;
        return [row];
      }
    );

    return {
      headers: currentData.headers,
      rows: [...currentData.rows, ...typicalRows],
    };
  }, [currentData, typicals]);

  // Fetch and parse CSV when the generated file key / storage URL is available
  useEffect(() => {
    if (!generatedFileKey || !storageUrl) {
      if (!generatedFileKey) {
        fetchedGeneratedFileKeyRef.current = null;
        setGeneratedData(null);
        setIsLoadingCSV(false);
      }
      return;
    }

    // Same file already loaded — ignore signed-URL rotation for this key.
    if (fetchedGeneratedFileKeyRef.current === generatedFileKey) {
      return;
    }

    // New file identity (e.g. regeneration): drop stale rows immediately so the
    // loading gate blocks edits against the previous generation.
    setGeneratedData(null);
    setCsvError(null);
    setIsLoadingCSV(true);

    const controller = new AbortController();
    const { signal } = controller;
    const fileKeyForFetch = generatedFileKey;
    const urlForFetch = storageUrl;

    async function fetchCSV() {
      try {
        const response = await fetch(urlForFetch, { signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch CSV: ${response.status}`);
        }
        const csvText = await response.text();
        const parsed = parseCSV(csvText);
        if (!signal.aborted) {
          fetchedGeneratedFileKeyRef.current = fileKeyForFetch;
          setGeneratedData(parsed);
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        if (!signal.aborted) {
          setCsvError(
            error instanceof Error ? error.message : "Failed to load CSV"
          );
          console.error("Error fetching CSV:", error);
        }
      } finally {
        if (!signal.aborted) {
          setIsLoadingCSV(false);
        }
      }
    }

    void fetchCSV();

    return () => {
      controller.abort();
    };
  }, [generatedFileKey, storageUrl]);

  // Hydrate edited payload
  useEffect(() => {
    let cancelled = false;

    if (editedPayloadRef === undefined) {
      return () => {
        cancelled = true;
      };
    }

    if (editedPayloadRef === null) {
      hydratedEditedRef.current = null;
      setEditedData(null);
      setEditedPayloadError(null);
      setIsHydratingEdited(false);
      return () => {
        cancelled = true;
      };
    }

    const payloadRef = editedPayloadRef as TableRowsPayloadRef;
    const alreadyHydrated =
      hydratedEditedRef.current?.tableId === payloadRef.tableId &&
      hydratedEditedRef.current?.rowsVersion === payloadRef.rowsVersion;

    // Same edited version already in memory — ignore download-URL rotation.
    if (alreadyHydrated) {
      return () => {
        cancelled = true;
      };
    }

    async function hydrateEditedPayload() {
      setIsHydratingEdited(true);
      setEditedPayloadError(null);
      try {
        const payload = await getTableRowsPayload(payloadRef);
        if (!cancelled) {
          hydratedEditedRef.current = {
            tableId: payloadRef.tableId,
            rowsVersion: payloadRef.rowsVersion,
          };
          setEditedData(payload);
        }
      } catch (error) {
        console.error("Error loading edited submittal payload:", error);
        if (!cancelled) {
          setEditedPayloadError("Failed to load edited submittal data.");
          setEditedData(null);
        }
      } finally {
        if (!cancelled) {
          setIsHydratingEdited(false);
        }
      }
    }

    void hydrateEditedPayload();
    return () => {
      cancelled = true;
    };
  }, [editedPayloadRef]);

  // Initialize version once from server state. Subsequent switches are owned by
  // explicit user actions (save / revert / tabs) so reactive query updates do
  // not remount the grid via the version-keyed React key — except when the
  // server clears edited data (e.g. regeneration), which must fall back to
  // generated so displayData does not keep reading a null edited payload.
  useEffect(() => {
    if (submittal == null) {
      return;
    }

    if (!hasInitializedVersionRef.current) {
      setActiveVersion(submittal.hasEditedData ? "edited" : "generated");
      hasInitializedVersionRef.current = true;
      return;
    }

    if (!submittal.hasEditedData) {
      hydratedEditedRef.current = null;
      setEditedData(null);
      setEditedPayloadError(null);
      setIsHydratingEdited(false);
      setActiveVersion((prev) => (prev === "edited" ? "generated" : prev));
    }
  }, [submittal]);

  const setEditedDataSafe = (data: ParsedData | null) => {
    if (data === null) {
      hydratedEditedRef.current = null;
    }
    setEditedData(data);
  };

  return {
    job,
    submittal,
    typicals,
    trackerItems,
    generatedData,
    editedData,
    displayData,
    activeVersion,
    setActiveVersion,
    hasEditedData,
    isLoadingCSV,
    csvError,
    isHydratingEdited,
    editedPayloadError,
    setEditedData: setEditedDataSafe,
    setEditedPayloadError,
  };
}
